import {
  VIEWBOX, SUN_PATH, MASSES, WINDOWS, BARS, SMALL,
} from './mark-15.geometry.mjs';

/**
 * MARK 15 — the primary logo mark.
 *
 * Dusk behind a commercial building, one office light still on, and the floor receding
 * in three spruce rules. Per the delivered board this IS the logo, not a decorative
 * device: "The #15 horizon and commercial building are the logo."
 *
 * Drawn in SVG rather than dropped in as <img> for three reasons, all of which the
 * earlier Shift Horizon device already proved out:
 *   - it inherits the page's tokens, so paper / dusk / night all work from one file
 *   - the one lit window can animate, which is the whole brand in a single gesture
 *   - no extra network request and no CSP question
 *
 * DENSITY. `detail="full"` is the real mark. `detail="compact"` collapses the 12-window
 * grid to the single lit window and drops the middle horizon rule — for anything under
 * ~48px, where twelve 0.5px windows stop being windows and become grey mud. Same
 * silhouette either way, so the two never read as different logos.
 */
export function Mark15({
  height,
  detail = 'full',
  animate = false,
  title,
  className,
}: {
  /**
   * Explicit pixel height. OMIT IT to let CSS size the mark — `.lockup .mark15` drives it
   * from `--m`, which is fluid, so the mark shrinks with the type on small screens instead
   * of staying at a hard height while everything around it scales. A hard height here is
   * what let the footer lockup blow past a 390px viewport.
   */
  height?: number;
  detail?: 'full' | 'compact';
  animate?: boolean;
  title?: string;
  className?: string;
}) {
  const compact = detail === 'compact';
  const bars = compact ? SMALL.bars : BARS;

  return (
    <svg
      height={height}
      width={height === undefined ? undefined : (height * VIEWBOX.w) / VIEWBOX.h}
      viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`}
      className={['mark15', animate ? 'is-animated' : '', className].filter(Boolean).join(' ')}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Sun first — the building is what cuts into it. */}
      <path className="m15-sun" d={SUN_PATH} />

      <g className="m15-mass">
        {MASSES.map((m, i) => (
          <rect key={i} x={m.x} y={m.y} width={m.w} height={m.h} />
        ))}
      </g>

      {compact ? (
        <rect
          className="m15-lit"
          x={SMALL.window.x}
          y={SMALL.window.y}
          width={SMALL.window.w}
          height={SMALL.window.h}
        />
      ) : (
        WINDOWS.map((w, i) => (
          <rect
            key={i}
            className={w.lit ? 'm15-lit' : 'm15-window'}
            x={w.x}
            y={w.y}
            width={w.w}
            height={w.h}
          />
        ))
      )}

      <g className="m15-bar">
        {bars.map((b, i) => (
          <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} />
        ))}
      </g>
    </svg>
  );
}
